/**
 * Investment metrics highlight box — hero metric(s) + optional breakdown table + "Other metrics" strip.
 * Reusable for cap-rate (buy/manage), rental arbitrage cash flow, and future variants.
 */
import React from 'react';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { COLOR_PRIMARY } from 'src/utils/const';
import { MetricsState } from '../../../propTypes';

/** Scroll target for "Adjust" operating-expenses links from breakdown rows. */
export const FORECAST_OPEX_ANCHOR_ID = 'forecast-operating-expenses-section';
/** Scroll target for loan/debt section links from breakdown rows. */
export const FORECAST_LOAN_ANCHOR_ID = 'forecast-loan-section';
/** Scroll target for startup cost (inside buy/rent section). */
export const FORECAST_STARTUP_ANCHOR_ID = 'forecast-startup-section';

/** KPI keys consumed by the buy/manage tabbed highlight; exclude from "Other metrics". */
export const BUY_MANAGE_CAP_LAYOUT_KEYS = new Set([
  'ltm_revenue',
  'annual_operating_expenses_total',
  'net_operating_income',
  'cap_rate',
  'cash_on_cash_return',
  'total_cash_invested',
  'mortgage_annual',
  'yearly_net_profit_before_tax',
  'management_fee_dollar',
  'opex_excluding_mgmt_fee',
  'owner_payout',
]);

/** KPI keys shown inside rental-arbitrage layout; exclude from "Other metrics". */
export const RENT_PROFIT_LAYOUT_KEYS = new Set([
  'ltm_revenue',
  'annual_operating_expenses_total',
  'yearly_net_profit_before_tax',
  'monthly_rent',
]);

/** Cap rate / ratios from API or derived KPIs may be 0–1 (decimal) or already a percent (e.g. 4.45). */
export function formatPercentMetric(val: number | null | undefined): string {
  if (val == null) return '--';
  const n = Number(val);
  if (Number.isNaN(n)) return '--';
  const asPct = n > 0 && n <= 1 ? n * 100 : n;
  return `${Math.round(asPct * 100) / 100}%`;
}

export function getTooltipForMetricKey(
  metricsState: { [key: string]: MetricsState },
  metricKey: string
): string | null {
  for (const section of Object.values(metricsState)) {
    if (!section) continue;
    for (const kpi of Object.values(section) as any[]) {
      if (kpi?.config?.key !== metricKey) continue;
      const tooltipLines = kpi.config.tooltipConfig?.definition || [];
      const formula = kpi.config.tooltipConfig?.formula;
      return [formula, ...tooltipLines].filter(Boolean).join(' — ') || null;
    }
  }
  return null;
}

export interface InvestmentMetricsHeroSpec {
  /** Stable key for React list */
  key: string;
  label: string;
  tooltip?: string | null;
  /** Primary value — string or custom node (e.g. large formatted %) */
  value: React.ReactNode;
  footnote?: React.ReactNode;
  /** primary = teal left border + gradient; muted = grey sidebar card */
  emphasis?: 'primary' | 'muted';
  /** When value is a simple string, use this font size token */
  valueVariant?: 'hero' | 'large';
}

export interface InvestmentMetricsBreakdownRowSpec {
  rowKey: string;
  label: React.ReactNode;
  value: string;
  /** Muted secondary line under the value (e.g. owner-occupied adjustment). */
  valueDetail?: React.ReactNode;
  tooltip?: string | null;
  emphasize?: boolean;
}

export interface InvestmentMetricsOtherMetricItem {
  key: string;
  label: string;
  unit: string;
  tooltip: string | null;
  value: any;
}

export interface InvestmentMetricsHighlightBoxProps {
  /** Optional wrapper id for tests / analytics */
  id?: string;
  /** One or two hero cards (e.g. cap rate + net profit) */
  heroes: InvestmentMetricsHeroSpec[];
  breakdown?: {
    title: string;
    description?: string;
    rows: InvestmentMetricsBreakdownRowSpec[];
  };
  /** Arbitrary content rendered after the breakdown (e.g. a graph). */
  customContent?: React.ReactNode;
  /** Optional trailing KPI chips */
  otherMetrics?: {
    sectionTitle?: string;
    items: InvestmentMetricsOtherMetricItem[];
    profitKeys?: string[];
    fmt: (val: number | null | undefined, unit: string) => string;
    periodLabel: string;
  };
}

function BreakdownTableRow({
  label,
  value,
  valueDetail,
  tooltip,
  emphasize,
}: Omit<InvestmentMetricsBreakdownRowSpec, 'rowKey'>) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        py: '10px',
        borderBottom: '1px solid #e5e7eb',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: emphasize ? '13px' : '12px',
            fontWeight: emphasize ? 600 : 500,
            color: '#374151',
          }}
        >
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} placement="top">
            <HelpOutlineRoundedIcon sx={{ fontSize: '14px', color: '#9ca3af', cursor: 'help' }} />
          </Tooltip>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: emphasize ? '14px' : '13px',
            fontWeight: 700,
            color: '#1f2937',
            textAlign: 'right',
          }}
        >
          {value}
        </Typography>
        {valueDetail != null && valueDetail !== '' && (
          <Typography
            component="div"
            sx={{
              fontSize: '11px',
              fontWeight: 500,
              color: '#9ca3af',
              mt: 0.25,
              textAlign: 'right',
              lineHeight: 1.35,
            }}
          >
            {valueDetail}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function HeroCard({ spec }: { spec: InvestmentMetricsHeroSpec }) {
  const isPrimary = spec.emphasis === 'primary';
  return (
    <Box
      key={spec.key}
      sx={{
        flex: isPrimary ? { md: '1.4' } : 1,
        minWidth: isPrimary ? 0 : '160px',
        p: '16px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        ...(isPrimary
          ? {
              borderLeft: `4px solid ${COLOR_PRIMARY}`,
              background: 'linear-gradient(135deg, #f0fdfa 0%, #f9fafb 100%)',
            }
          : { background: '#f9fafb' }),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
          }}
        >
          {spec.label}
        </Typography>
        {spec.tooltip && (
          <Tooltip title={spec.tooltip} placement="top">
            <HelpOutlineRoundedIcon sx={{ fontSize: '15px', color: '#9ca3af', cursor: 'help' }} />
          </Tooltip>
        )}
      </Box>
      {typeof spec.value === 'string' || typeof spec.value === 'number' ? (
        <Typography
          sx={{
            fontSize:
              spec.valueVariant === 'hero'
                ? { xs: '28px', sm: '32px' }
                : '22px',
            fontWeight: spec.valueVariant === 'hero' ? 800 : 700,
            color: spec.emphasis === 'primary' ? COLOR_PRIMARY : '#1f2937',
            lineHeight: spec.valueVariant === 'hero' ? 1.1 : undefined,
          }}
        >
          {spec.value}
        </Typography>
      ) : (
        spec.value
      )}
      {spec.footnote && (
        <Box sx={{ mt: spec.valueVariant === 'hero' ? 1 : 0.75 }}>
          {typeof spec.footnote === 'string' ? (
            <Typography sx={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.4 }}>{spec.footnote}</Typography>
          ) : (
            spec.footnote
          )}
        </Box>
      )}
    </Box>
  );
}

/**
 * Renders "Adjust" link that scrolls to operating expenses in Forecast Your Income.
 */
export function OperatingExpensesAdjustLink({ label = 'Adjust' }: { label?: string } = {}) {
  return (
    <Link
      component="button"
      type="button"
      onClick={() =>
        document.getElementById(FORECAST_OPEX_ANCHOR_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      sx={{
        fontSize: '11px',
        ml: 0.5,
        verticalAlign: 'baseline',
        cursor: 'pointer',
        color: COLOR_PRIMARY,
      }}
    >
      {label}
    </Link>
  );
}

/**
 * Renders "Set up loan" link that scrolls to the loan/debt input section.
 */
export function LoanSetupLink() {
  return (
    <Link
      component="button"
      type="button"
      onClick={() =>
        document.getElementById(FORECAST_LOAN_ANCHOR_ID)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
      sx={{
        fontSize: '11px',
        ml: 0.5,
        verticalAlign: 'baseline',
        cursor: 'pointer',
        color: COLOR_PRIMARY,
      }}
    >
      Set up loan
    </Link>
  );
}

/**
 * Renders "Edit" link that scrolls to the startup cost input section.
 */
export function StartupCostSetupLink() {
  return (
    <Link
      component="button"
      type="button"
      onClick={() =>
        document.getElementById(FORECAST_STARTUP_ANCHOR_ID)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
      sx={{
        fontSize: '11px',
        ml: 0.5,
        verticalAlign: 'baseline',
        cursor: 'pointer',
        color: COLOR_PRIMARY,
      }}
    >
      Edit
    </Link>
  );
}

/** Use after minus sign in breakdown value column to avoid double "\u2212". */
export function stripLeadingMinus(formatted: string): string {
  return formatted.replace(/^-/, '');
}

export function BreakdownResultChevron() {
  return (
    <ChevronRightIcon sx={{ fontSize: 16, verticalAlign: 'middle', color: '#9ca3af', ml: 0.25 }} />
  );
}

export default function InvestmentMetricsHighlightBox({
  id,
  heroes,
  breakdown,
  customContent,
  otherMetrics,
}: InvestmentMetricsHighlightBoxProps) {
  const profitKeys = otherMetrics?.profitKeys ?? ['yearly_net_profit_before_tax', 'net_operating_income'];

  return (
    <Box id={id} sx={{ mb: '20px' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: '12px',
          mb: '12px',
        }}
      >
        {heroes.map(h => (
          <HeroCard key={h.key} spec={h} />
        ))}
      </Box>

      {breakdown && (
        <Box
          sx={{
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            p: '14px 16px',
            background: '#fff',
            mb: customContent || otherMetrics?.items?.length ? '12px' : 0,
          }}
        >
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1f2937', mb: 0.5 }}>
            {breakdown.title}
          </Typography>
          {breakdown.description && (
            <Typography sx={{ fontSize: '11px', color: '#9ca3af', mb: 1.5 }}>
              {breakdown.description}
            </Typography>
          )}
          {breakdown.rows.map(({ rowKey, ...row }) => (
            <BreakdownTableRow key={rowKey} {...row} />
          ))}
        </Box>
      )}

      {customContent}

      {otherMetrics && otherMetrics.items.length > 0 && (
        <>
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', mb: '8px', mt: '4px' }}>
            {otherMetrics.sectionTitle ?? 'Other metrics'}
          </Typography>
          <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {otherMetrics.items.map(metric => (
              <Box
                key={metric.key}
                sx={{
                  flex: '1 1 0',
                  minWidth: '120px',
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', mb: '4px' }}>
                  <Typography
                    sx={{
                      fontSize: '10px',
                      fontWeight: 500,
                      color: '#4b5563',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                    }}
                  >
                    {metric.label}
                    {metric.unit === '$' ? ` ${otherMetrics.periodLabel}` : ''}
                  </Typography>
                  {metric.tooltip && (
                    <Tooltip title={metric.tooltip} placement="top">
                      <HelpOutlineRoundedIcon sx={{ fontSize: '13px', color: '#9ca3af', cursor: 'help' }} />
                    </Tooltip>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color:
                      profitKeys.includes(metric.key) && metric.value != null
                        ? metric.value >= 0
                          ? '#059669'
                          : '#dc2626'
                        : '#1f2937',
                  }}
                >
                  {otherMetrics.fmt(metric.value, metric.unit)}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
